import { GetPricingBookmarksQuery } from '@/types';
import {
  HydrateBookmarkEntry,
  usePricingBookmarksStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-bookmarks-store';
import { useBusinessOrganizationPricingConfig } from '@sales-portal/blocs/hooks/use-business-organization-pricing-config';
import { useGeneratedGetPricingBookmarksQuery } from '@sales-portal/domain/pricing/blocs/documents/get-pricing-bookmarks.generated';
import { useIsValidPricingLayout } from '@sales-portal/domain/pricing/blocs/hooks/use-is-valid-pricing-layout';
import { useMemo } from 'react';

type PricingBookmarkRow =
  GetPricingBookmarksQuery['getPricingBookmarks']['bookmarks'][number];

// A usable bookmark row resolves to a concrete (variant pack, restaurant) pair
// with a timestamp. The returned rows/count AND the store hydration both filter
// on this, so the sidebar count and row icons can't drift from the tab data.
const isCompleteBookmarkRow = (row: PricingBookmarkRow): boolean =>
  Boolean(row.variant_pack?.uuid && row.restaurant?.uuid && row.bookmarked_at);

// Safe only for rows that passed isCompleteBookmarkRow; the ?? fallbacks never
// apply in practice but satisfy the (non-narrowing) predicate.
const toHydrateEntry = (row: PricingBookmarkRow): HydrateBookmarkEntry => ({
  bookmarkedAt: row.bookmarked_at ?? '',
  note: row.bookmark_note ?? null,
  restaurantUUID: row.restaurant?.uuid ?? '',
  variantPackUUID: row.variant_pack?.uuid ?? '',
});

// Single pass: keep only complete rows and shape them for the store.
const bookmarkEntriesFromRows = (
  rows: PricingBookmarkRow[],
): HydrateBookmarkEntry[] =>
  rows.reduce<HydrateBookmarkEntry[]>((entries, row) => {
    if (isCompleteBookmarkRow(row)) {
      entries.push(toHydrateEntry(row));
    }
    return entries;
  }, []);

// Fetches the user's bookmarks whenever bookmarking is enabled and hydrates the
// optimistic store from the authoritative read. The enriched rows feed the
// Bookmarked tab; the store feeds row icons and the sidebar count.
//
// `revalidate` is for the screen that renders the rows: bookmarking doesn't
// refetch this query, so a cached list can predate the newest bookmark. Reading
// it through the cache while a network read is in flight keeps `loading` true,
// so the table shows its skeleton instead of a stale (possibly empty) list.
export const usePricingBookmarksLoader = ({
  revalidate = false,
}: { revalidate?: boolean } = {}) => {
  const { hasBookmarkingAccess } = useBusinessOrganizationPricingConfig();
  const hydrate = usePricingBookmarksStore((store) => store.hydrate);
  // The sidebar calls this unconditionally, so skip on phones.
  const isValidPricingLayout = useIsValidPricingLayout();

  const { data, loading, error, refetch } =
    useGeneratedGetPricingBookmarksQuery({
      fetchPolicy: revalidate ? 'cache-and-network' : 'cache-first',
      onCompleted: (result) =>
        hydrate(bookmarkEntriesFromRows(result.getPricingBookmarks.bookmarks)),
      skip: !hasBookmarkingAccess || !isValidPricingLayout,
    });

  return useMemo(() => {
    const bookmarks = (data?.getPricingBookmarks.bookmarks ?? []).filter(
      isCompleteBookmarkRow,
    );
    return {
      bookmarks,
      count: bookmarks.length,
      data,
      error,
      hasBookmarks: bookmarks.length > 0,
      loading,
      refetch,
    };
  }, [data, error, loading, refetch]);
};
